/**
 * SetOpsSelector Component
 * User-friendly modal for selecting set operation type and match mode
 */

import { useState, useEffect } from 'react';
import { X, Plus, Minus, Search, Shuffle, Check, ChevronDown, Info } from 'lucide-react';
import { Button } from '../shared/Button';

/**
 * Set operation types matching backend enum
 */
export type SetOperationType = 'union' | 'intersection' | 'difference' | 'symmetric_difference';

/**
 * Match mode types matching backend enum
 */
export type MatchMode = 'strict' | 'isrc' | 'fuzzy' | 'smart';

/**
 * Preview counts for each operation type
 */
export interface SetOpsPreview {
  union: number;
  intersection: number;
  difference: number;
  symmetric_difference: number;
}

export interface SetOpsSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (operation: SetOperationType, matchMode: MatchMode) => void;
  preview: SetOpsPreview | null;
  sourceCount: number;
  targetCount: number;
  sourceName: string;
  targetName: string;
  isLoading?: boolean;
}

/**
 * Operation configuration with user-friendly display metadata
 */
interface OperationConfig {
  type: SetOperationType;
  label: string;
  getDescription: (source: string, target: string) => string;
  icon: typeof Plus;
  iconColor: string;
  bgColor: string;
  hoverBg: string;
  borderColor: string;
  technicalLabel: string;
}

/**
 * All available set operations with user-friendly UI metadata
 */
const OPERATIONS: OperationConfig[] = [
  {
    type: 'union',
    label: 'All Tracks',
    getDescription: (source, target) =>
      `Combines "${source}" and "${target}" into one list, without duplicates`,
    icon: Plus,
    iconColor: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    hoverBg: 'hover:bg-blue-500/20',
    borderColor: 'border-blue-500/30 hover:border-blue-500',
    technicalLabel: 'A ∪ B',
  },
  {
    type: 'intersection',
    label: 'Common Tracks',
    getDescription: (source, target) =>
      `Shows only tracks found in both "${source}" and "${target}"`,
    icon: Search,
    iconColor: 'text-green-400',
    bgColor: 'bg-green-500/10',
    hoverBg: 'hover:bg-green-500/20',
    borderColor: 'border-green-500/30 hover:border-green-500',
    technicalLabel: 'A ∩ B',
  },
  {
    type: 'difference',
    label: 'First Without Second',
    getDescription: (source, target) =>
      `Removes from "${source}" all tracks that are also in "${target}"`,
    icon: Minus,
    iconColor: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    hoverBg: 'hover:bg-orange-500/20',
    borderColor: 'border-orange-500/30 hover:border-orange-500',
    technicalLabel: 'A − B',
  },
  {
    type: 'symmetric_difference',
    label: 'Unique to Either (remove common)',
    getDescription: (source, target) =>
      `Tracks in either "${source}" or "${target}" — but not in both`,
    icon: Shuffle,
    iconColor: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    hoverBg: 'hover:bg-purple-500/20',
    borderColor: 'border-purple-500/30 hover:border-purple-500',
    technicalLabel: 'A △ B',
  },
];

/**
 * Match mode configuration with user-friendly descriptions
 */
interface MatchModeConfig {
  mode: MatchMode;
  label: string;
  description: string;
}

const MATCH_MODES: MatchModeConfig[] = [
  {
    mode: 'smart',
    label: 'Smart (recommended)',
    description: 'Matches tracks by ISRC code and name',
  },
  {
    mode: 'strict',
    label: 'Strict',
    description: 'Only exact same version on Spotify',
  },
  {
    mode: 'isrc',
    label: 'ISRC Code',
    description: 'Same recording across services',
  },
  {
    mode: 'fuzzy',
    label: 'By Name',
    description: 'Matches by artist and track name',
  },
];

export function SetOpsSelector({
  isOpen,
  onClose,
  onSelect,
  preview,
  sourceCount,
  targetCount,
  sourceName,
  targetName,
  isLoading = false,
}: SetOpsSelectorProps) {
  const [selectedOperation, setSelectedOperation] = useState<SetOperationType | null>(null);
  const [selectedMatchMode, setSelectedMatchMode] = useState<MatchMode>('smart');
  const [showMatchModeInfo, setShowMatchModeInfo] = useState(false);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOperation(null);
      setSelectedMatchMode('smart');
      setShowMatchModeInfo(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedOperation) {
      onSelect(selectedOperation, selectedMatchMode);
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && selectedOperation) {
      handleConfirm();
    }
  };

  // Get dynamic label for difference operation
  const getOperationLabel = (op: OperationConfig): string => {
    if (op.type === 'difference') {
      return `"${sourceName}" ilman "${targetName}"`;
    }
    return op.label as string;
  };

  const selectedMatchModeConfig = MATCH_MODES.find(m => m.mode === selectedMatchMode);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 flex items-center justify-center z-50 p-4"
        onKeyDown={handleKeyDown}
      >
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
            <h2 className="text-base font-semibold text-zinc-100">
              Combine Playlists
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Source/Target indicator */}
            <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg text-sm">
              <div className="flex-1 text-center">
                <div className="font-medium text-zinc-100 truncate" title={sourceName}>
                  {sourceName}
                </div>
                <div className="text-xs text-zinc-500">{sourceCount} tracks</div>
              </div>
              <div className="text-zinc-600 text-lg">→</div>
              <div className="flex-1 text-center">
                <div className="font-medium text-zinc-100 truncate" title={targetName}>
                  {targetName}
                </div>
                <div className="text-xs text-zinc-500">{targetCount} tracks</div>
              </div>
            </div>

            {/* Operation buttons */}
            <div className="space-y-2">
              {OPERATIONS.map((op) => {
                const isSelected = selectedOperation === op.type;
                const count = preview?.[op.type];
                const Icon = op.icon;

                return (
                  <button
                    key={op.type}
                    onClick={() => setSelectedOperation(op.type)}
                    className={`
                      relative w-full p-3 rounded-lg border-2 transition-all text-left
                      ${isSelected
                        ? `${op.bgColor} ${op.borderColor.split('hover:')[1]} ring-1 ring-offset-1 ring-offset-zinc-900`
                        : `bg-zinc-800/30 border-zinc-700/50 ${op.hoverBg} hover:border-zinc-600`
                      }
                    `}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${op.bgColor} flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${op.iconColor}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-medium text-zinc-100">
                            {getOperationLabel(op)}
                          </h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {count !== undefined && (
                              <span className="text-xs font-medium text-zinc-400 tabular-nums">
                                {isLoading ? (
                                  <span className="inline-block w-8 h-3 bg-zinc-700 animate-pulse rounded" />
                                ) : (
                                  `${count} tracks`
                                )}
                              </span>
                            )}
                            {isSelected && (
                              <div className="p-0.5 rounded-full bg-emerald-500">
                                <Check className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                          {op.getDescription(sourceName, targetName)}
                        </p>
                        <div className="mt-1 text-[10px] font-mono text-zinc-600">
                          {op.technicalLabel}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer with match mode dropdown */}
          <div className="px-5 py-4 border-t border-zinc-800 bg-zinc-900/50 space-y-3">
            {/* Match mode selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">Match mode:</span>
              <div className="relative">
                <select
                  value={selectedMatchMode}
                  onChange={(e) => setSelectedMatchMode(e.target.value as MatchMode)}
                  className="appearance-none bg-zinc-800 border border-zinc-700 rounded-md pl-2.5 pr-7 py-1 text-xs text-zinc-200 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/50 cursor-pointer"
                >
                  {MATCH_MODES.map((mode) => (
                    <option key={mode.mode} value={mode.mode}>
                      {mode.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500 pointer-events-none" />
              </div>
              <button
                type="button"
                onClick={() => setShowMatchModeInfo(!showMatchModeInfo)}
                className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
                aria-label="Show more info"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Match mode info tooltip */}
            {showMatchModeInfo && selectedMatchModeConfig && (
              <div className="text-xs text-zinc-400 bg-zinc-800/50 rounded-md px-2.5 py-1.5">
                {selectedMatchModeConfig.description}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={!selectedOperation}
                isLoading={isLoading}
              >
                Execute
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
